import React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { InView } from 'react-intersection-observer';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import { yieldCallback } from '@zillow/yield-callback';

// Actions
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Components
import AdvertisementWrapper from 'app/shared/modules/AdvertisementWrapper';
import InfoCard from 'app/shared/modules/MobileSRP/ListingCards/InfoCard';
import ListingWrapper from 'app/shared/modules/listing-cards/ListingWrapper';
import NoListingsFound from 'app/shared/modules/NoListingsFound/NoListingsFound';
import Pagination from 'app/shared/modules/Pagination/Pagination/component';
import SkeletonListingCardGroup from 'app/shared/modules/skeleton/SkeletonListingCardGroup';

// Misc / Utils
import { DEFAULT } from 'app/shared/models/Filter';
import { areaUtils_getSrpMaxPageLimit } from 'app/shared/utils/areaUtils';
import commuteUtils from 'app/shared/utils/commuteUtils';
import constants from 'app/shared/constants/ConstantsBundle';
import './style.scss';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import { noop } from 'lodash';

// Types
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';

const PAGINATION_UPPER_BOUND = 20;

type HideNativeAdState = {
  slot1: boolean;
  slot2: boolean;
};

type OwnProps = {
  maxListingsPerPage: number;
  setMapView?: () => void;
  isSkeleton?: boolean;
};

type AreaListingsContainerState = {
  hideNativeAd: HideNativeAdState;
  commuteTimesArray: Array<number>;
};

const mapStateToProps = (state: RootReduxState) => {
  const activeUserPoint = state.user.userPoints.activeUserPoint;
  const maxBuildings = state.listings.totalBuildings;
  const userPoints = state.user.userPoints.destinations as unknown as Record<string, unknown>;
  // activeUserPoint can be string | null, handle null case
  const userPoint = activeUserPoint !== null ? userPoints[activeUserPoint] : null;
  const listings = state.listings.listingGroups.byCoords;

  return {
    currPage: Number(state.location.current.query.page || 1),
    isInitialSsrPage: state.app.isInitialSsrPage,
    filter: state.filter,
    isMobile: state.app.device.screenWidth === 'sm',
    listingsLoading: !state.app.fetchListingsByCoordsComplete,
    listings,
    maxPage: Math.max(Math.ceil(maxBuildings / constants.MAX_LISTINGS_PER_PAGE), 1),
    userPoint,
  };
};

// Use ThunkDispatch for proper thunk action typing
const mapDispatchToProps = (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>) => ({
  dispatch,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type AreaListingsContainerProps = PropsFromRedux & OwnProps;

class AreaListingsContainer extends React.Component<AreaListingsContainerProps, AreaListingsContainerState> {
  static defaultProps = {
    listings: [],
    isSkeleton: false,
    maxListingsPerPage: 0,
    setMapView: noop,
    listingsLoading: false,
    currPage: 1,
    maxPage: 1,
    dispatch: noop,
    userPoint: null,
  };

  constructor(props: AreaListingsContainerProps) {
    super(props);

    this.state = {
      hideNativeAd: {
        slot1: false,
        slot2: false,
      },
      commuteTimesArray: [],
    };
  }

  componentDidMount() {
    const { filter, userPoint } = this.props;

    if (commuteUtils.isCommuteFilterSet(filter.commute) || userPoint) {
      this.fetchCommuteTimes();
    }
  }

  componentDidUpdate(prevProps: AreaListingsContainerProps) {
    const { currPage, filter, listings, userPoint } = this.props;
    const prevFilter = prevProps.filter;
    const commuteFilterSet = commuteUtils.isCommuteFilterSet(filter.commute);
    const listingsLoaded = isEmpty(prevProps.listings) && !isEmpty(listings);
    const pageChanged = prevProps.currPage !== currPage;

    const commuteFilterChanged =
      filter.commute.commuteTimeMode !== prevFilter.commute.commuteTimeMode ||
      filter.commute.commuteTime !== prevFilter.commute.commuteTime ||
      filter.commute.commuteMode !== prevFilter.commute.commuteMode ||
      filter.commute.commuteLats !== prevFilter.commute.commuteLats ||
      filter.commute.commuteLons !== prevFilter.commute.commuteLons;

    if (
      (listingsLoaded && (commuteFilterSet || userPoint)) ||
      (commuteFilterChanged && (commuteFilterSet || userPoint)) ||
      (pageChanged && (commuteFilterSet || userPoint))
    ) {
      this.fetchCommuteTimes();
    }
  }

  fetchCommuteTimes = () => {
    const { dispatch, listings, filter, userPoint } = this.props;

    if (!userPoint || isEmpty(listings)) {
      return;
    }

    const commuteTimesArray: Array<number> = [];

    const listingsGroup1 = listings.slice(0, 10);
    const listingsGroup2 = listings.slice(10, 20);
    // commuteUtils is untyped (@ts-nocheck), using never to bypass type inference
    const locationParams1 = commuteUtils.buildCommuteTimesRequest(
      filter.commute,
      listingsGroup1 as never,
      userPoint as never,
    );
    let locationParams2: typeof locationParams1 | undefined;

    if (!isEmpty(listingsGroup2)) {
      locationParams2 = commuteUtils.buildCommuteTimesRequest(
        filter.commute,
        listingsGroup2 as never,
        userPoint as never,
      );
    }

    // dispatch returns a thunk promise for async actions
    (dispatch(ListingEngineActions.getCommuteTime(locationParams1)) as Promise<Array<Record<string, number>>>)
      .then((result: Array<Record<string, number>> = []) => {
        const formatCommuteMode = filter.commute.commuteMode ? filter.commute.commuteMode.toLowerCase() : 'driving';

        result.forEach((travelTime) => {
          commuteTimesArray.push(travelTime[formatCommuteMode]);
        });

        this.setState({ commuteTimesArray });

        if (locationParams2) {
          return dispatch(ListingEngineActions.getCommuteTime(locationParams2)) as Promise<
            Array<Record<string, number>>
          >;
        }
        return [];
      })
      .then((result: Array<Record<string, number>> = []) => {
        if (isEmpty(result)) {
          return;
        }

        const formatCommuteMode = filter.commute.commuteMode ? filter.commute.commuteMode.toLowerCase() : 'driving';

        result.forEach((travelTime) => {
          commuteTimesArray.push(travelTime[formatCommuteMode]);
        });
        this.setState({ commuteTimesArray });
        return undefined;
      })
      .catch((error: Error) => {
        this.props.dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.commuteTimes.calcTransitTimes',
            errorClass: 'listingEngineActions',
          }),
        );
      });
  };

  hideAdElement = (slot: keyof HideNativeAdState, hideNativeAd: boolean) => {
    if (this.state.hideNativeAd[slot] !== hideNativeAd) {
      this.setState({
        hideNativeAd: {
          ...this.state.hideNativeAd,
          [slot]: hideNativeAd,
        },
      });
    }
  };

  yieldCurrentSearchClick = yieldCallback(() => {
    const { dispatch } = this.props;
    dispatch(UserSearchActions.listingOnCurrentSearchClicked());
  });

  handleListingClick = (listing: ListingDetails) => {
    emitTrackClicksIntoPropertyEvent({ listing, triggerLocation: 'search_results_list' });
    this.yieldCurrentSearchClick();
  };

  trackImpression = (listing: ListingDetails) => {
    emitTrackImpressionForPropertyCardEvent({ listing, triggerLocation: 'search_results_list' });
  };

  renderListingsArray = (shownListings: Array<ListingDetails>) => {
    const { currPage, filter, isSkeleton, listingsLoading, maxPage } = this.props;
    const hasActivePriceFilter = filter.price.max !== DEFAULT.price.max && filter.price.min !== DEFAULT.price.min;
    const hasActiveBedFilter = !isEqual(filter.bedrooms, DEFAULT.bedrooms);
    const bothInfoCardFiltersActive = hasActivePriceFilter && hasActiveBedFilter;
    const shouldRenderInfoCards =
      ((currPage === 1 && maxPage > 1) || isSkeleton || listingsLoading) && !bothInfoCardFiltersActive;
    const INFO_CARD_LOCATION = this.state.hideNativeAd?.slot1 ? 10 : 11;

    const renderedListings = map(shownListings, (listing, idx) => {
      const shouldLazyLoadListingCardPhotos = idx > 5;

      if (listing) {
        return (
          <ListingWrapper
            keepMapLocation
            key={listing.aliasEncoded}
            listing={listing}
            shouldLazyLoadListingCardPhotos={shouldLazyLoadListingCardPhotos}
            onClick={() => this.handleListingClick(listing)}
            commuteTime={this.state.commuteTimesArray[idx]}
            // ListingWrapper has mismatched types with ListingDetails vs ListingType
            trackImpression={this.trackImpression as never}
            triggerLocation="search_results"
            triggerObject="property_card"
          />
        );
      }
    });

    if (shouldRenderInfoCards) {
      renderedListings.splice(INFO_CARD_LOCATION, 0, <InfoCard key="InfoCards" />);
    }

    if (shownListings.length > 2 && currPage === 1) {
      return (
        <AdvertisementWrapper
          handleHideAdElement={this.hideAdElement}
          hideSlot1={this.state.hideNativeAd.slot1}
          hideSlot2={this.state.hideNativeAd.slot2}
        >
          {renderedListings}
        </AdvertisementWrapper>
      );
    }

    return renderedListings;
  };

  render() {
    const { listings, isSkeleton, listingsLoading, currPage, maxPage, filter, maxListingsPerPage } = this.props;

    const nextPage = currPage + 1;
    const prevPage = currPage - 1;
    const startSlice = 0;
    const endSlice = maxListingsPerPage;
    const shownListings = listings.slice(startSlice, endSlice);

    const maxPageLimit = areaUtils_getSrpMaxPageLimit(maxPage, filter.search.slug);

    if ((shownListings.length < 1 && listingsLoading) || isSkeleton) {
      return (
        <div className="AreaListingsContainer-listings">
          <SkeletonListingCardGroup count={20} />
        </div>
      );
    }

    if (shownListings.length < 1) {
      return <NoListingsFound />;
    }

    const renderedListingsArray = this.renderListingsArray(shownListings);

    return (
      <>
        <ul className="AreaListingsContainer-listings">{renderedListingsArray}</ul>
        {/* Handles GA tracking when user scrolls to bottom of Listings Container */}
        <InView as="span" triggerOnce />
        <Pagination />
      </>
    );
  }
}

export default connector(AreaListingsContainer);
